import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectAssessmentScaleGroups } from 'store/options';
import { useAssessmentScaleGroupOptions } from 'hooks/GoalsAndSubgoalsForm/useGoalAssessmentValues';

/**
 * Returns a resolver for a subgoal's assessment-scale options, each carrying
 * the same hint the edit form shows.
 *
 * Only active scales are offered, but the scale already in use is kept even
 * once it has been retired — otherwise the control renders blank, and picking
 * any option would silently discard a scale the subgoal is legitimately on.
 *
 * A resolver rather than a list, because the goal view builds its subgoal rows
 * inside a render callback and cannot call a hook per subgoal.
 */
export default function useAssessmentScaleOptions() {
  const activeOptions = useAssessmentScaleGroupOptions();
  const allGroups = useSelector(selectAssessmentScaleGroups);

  return useCallback(
    (currentScaleId?: number) => {
      const active = activeOptions ?? [];
      if (active.some(({ id }) => id === currentScaleId)) return active;

      // Retired scales carry no hint, which is correct: there is nothing to
      // explain about a scale that can no longer be chosen.
      const retired = (allGroups ?? []).find(({ id }) => id === currentScaleId);

      return retired ? [...active, retired] : active;
    },
    [activeOptions, allGroups],
  );
}
